import {
    Card,
    Row,
    Col,
    List,
    Timeline,
    Badge,
    Tag,
    Flex,
    Tooltip,
  } from "antd";
  import { useEffect, useState } from "react";
  import classes from "./command.module.scss";
  import { Typography } from "antd";
  import RadarChart from "./RadarChart";
  const { Title, Text, Paragraph } = Typography;
  
  const CommandSystemCmp = () => {
    // Dummy Data
    const radarPointsRisk = [
      {
        title: "Hidden Client Churn Signal (Pattern Detection)",
        risk: "3 mid-tier clients haven’t submitted a support ticket or invoice payment in 21+ days — historically, this pattern precedes churn within 30 days.",
        trigger: "Detected drop in Stripe activity + Asana ticket silence.",
        impact: "$22,000/month at risk ",
        mitigation: [
          "Flagged AMs in Slack",
          "Pre-written re-engagement emails deployed",
          "Tasks scheduled for check-in calls",
        ],
      },
      {
        title: "Undetected Scope Creep Breach in Active Project",
        risk: "The “Buildify Migration” project has exceeded 28% more engineer hours than scoped — billable overages not being tracked.",
        trigger: "Asana task overages + QuickBooks time entries mismatched",
        impact: "Margin loss + potential client conflict",
        mitigation: [
          "Slack alert to PM",
          "Scope audit task deployed",
          "Email template pre-filled to client re: amendment",
        ],
      },
      {
        title: "Burnout Spike Among Tier-1 Engineers",
        risk: "Two engineers have completed 37% more tickets than team average for three consecutive weeks — no PTO scheduled.",
        trigger: "Asana velocity pattern",
        impact: "Retention risk + SLA degradation",
        mitigation: [
          "PTO recommendation generated",
          "Workload redistributed",
          "HR follow-up scheduled",
        ],
      },
      {
        title: "Security Risk: Unpatched Vulnerability Across 6 Clients",
        risk: "Missed security patch reported in 6 client environments — tied to a new CVE released this week.",
        trigger: "Cross-match from support tickets + CVE feed",
        impact: "Potential breach exposure + legal risk",
        mitigation: [
          "Security playbook auto-deployed",
          "Jira tasks created for patch rollout",
          "Legal disclaimer alert drafted for optional disclosure",
        ],
      },
    ];
  
    const radarPointsOpportunity = [
      {
        title: "Emerging Trend in Security = Product Expansion",
        oppurnity:
          "23% rise in SMB clients requesting endpoint monitoring over past 60 days.",
        trigger: "Ticket keyword clustering",
        impact: "$140K projected ARR from add-on",
        execution: [
          "New service package drafted",
          "Marketing page skeleton created in Notion",
          "Pilot outreach to 5 active clients launched",
        ],
      },
      {
        title: "Missed Upsell: 12 Clients on Starter Plan Using Premium Features",
        oppurnity:
          "Feature usage shows 12 clients exceeding limits but haven’t upgraded.",
        trigger: "Internal usage logs + QBO billing mismatch",
        impact: "$3,600/mo lost revenue",
        execution: [
          "Upgrade email sent",
          "Account managers pinged",
          "Stripe SKU swap automation triggered",
        ],
      },
      {
        title: "Competitor Exit Creates Opening for Strategic Takeover",
        oppurnity:
          "Regional competitor “EdgeSecure” shut down — 18 known shared clients now vendorless.",
        trigger: "RSS scrape + CRM match",
        impact: "$72K potential new revenue",
        execution: [
          "Pre-drafted LinkedIn outreach sent",
          "Competitor switch-over checklist generated",
          "Sales leader brief auto-created",
        ],
      },
      {
        title: "Referral Spike Pattern Detected",
        oppurnity:
          "3 client referrals logged in past week — correlated to NPS survey surge",
        trigger: "CRM + Survey tool merge",
        impact: "Viral momentum potential",
        execution: [
          "Referral rewards page auto-published",
          "Emails sent to top 5 promoters",
          "Slack message to sales: “Lean in — trend spotted”",
        ],
      },
    ];
  
    const autoExecuted = [
      { id: 1, title: "Auto-Patch Applied", priority: "Critical" },
      { id: 2, title: "Vendor SLA Issue", priority: "High" },
      { id: 3, title: "Firewall Breach Attempt", priority: "Critical" },
      { id: 4, title: "Unauthorized Access Prevented", priority: "Medium" },
    ];
  
    const initialMissionStack = [
      { id: 2, x: 30, y: 40, type: "Opportunity" },
      { id: 3, x: 25, y: 15, type: "Risk" },
    ];
  
    const initialFeed = [
      "System Boot Successful",
      "Scheduled Scan Started",
      "Detected Risk: SQL Injection Attempt",
      "Auto-executed: Applied Firewall Rule",
      "Detected Opportunity: SLA Optimization",
      "Mission Added: SQL Injection",
      "Auto-executed: Vendor SLA Issue",
      "Detected Risk: Malware Signature",
      "User Executed Mission: Patch Deployment",
      "Detected Risk: Ransomware Pattern",
      "Auto-executed: Suspicious IP Blocked",
      "Mission Added: Malware Mitigation",
    ];
  
    const [time, setTime] = useState(new Date());
    const [missionStack, setMissionStack] = useState(initialMissionStack);
    const [feed, setFeed] = useState(initialFeed);
  
    useEffect(() => {
      const timer = setInterval(() => setTime(new Date()), 1000);
      return () => clearInterval(timer);
    }, []);
  
    return (
      <>
        {" "}
        <div className={classes.header}>
          <Title level={4} style={{ marginTop: 0 }}>
            Radar+
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            The Radar+ serves as a live command center, providing
            real-time monitoring and actionable controls. It offers instant
            visibility into system status, events, and operations, allowing users
            to proactively manage activities, respond to incidents, and execute
            critical actions directly from a centralized dashboard. Designed for
            efficiency and responsiveness, it ensures teams can maintain full
            situational awareness and take immediate, informed decisions when
            needed.
          </Text>
        </div>
        <div style={{ padding: "20px" }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card title="System Status on Radar" className={"radarContainer"}>
                <RadarChart />
                <div>
                  <div
                    style={{
                      flexDirection: "column",
                      display: "flex",
                      gap: 15,
                      height: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <div>
                      <Title level={5} style={{ color: "white" }}>
                        Top 4 Risks
                      </Title>
  
                      <Flex gap="10px 0" wrap>
                        {radarPointsRisk.map((item, index) => (
                          <Tooltip
                            title={
                              <div
                                style={{ backgroundColor: "black", padding: 5 }}
                              >
                                <Title level={5} style={{ color: "white" }}>
                                  Risk:{" "}
                                </Title>
                                <Paragraph style={{ color: "white" }}>
                                  {item.risk}
                                </Paragraph>
                                <Title level={5} style={{ color: "white" }}>
                                  Trigger:{" "}
                                </Title>
                                <Paragraph style={{ color: "white" }}>
                                  {item.trigger}
                                </Paragraph>
                                <Title level={5} style={{ color: "white" }}>
                                  Impact:{" "}
                                </Title>
                                <Paragraph style={{ color: "white" }}>
                                  {item.impact}
                                </Paragraph>
                                <Title level={5} style={{ color: "white" }}>
                                  Auto Execution{" "}
                                </Title>
                                <List
                                  size="small"
                                  bordered
                                  dataSource={item.mitigation}
                                  renderItem={(item) => (
                                    <List.Item style={{ color: "white" }}>
                                      {item}
                                    </List.Item>
                                  )}
                                />
                              </div>
                            }
                          >
                            <Tag
                              key={`${index}-risk`}
                              bordered={false}
                              color="error"
                            >
                              {item.title}
                            </Tag>
                          </Tooltip>
                        ))}
                      </Flex>
                    </div>
                    <div>
                      <Title level={5} style={{ color: "white" }}>
                        Top 4 Opportunities
                      </Title>
  
                      <Flex gap="10px 0" wrap>
                        {radarPointsOpportunity.map((item, index) => (
                          <Tooltip
                            title={
                              <div
                                style={{ backgroundColor: "black", padding: 5 }}
                              >
                                <Title level={5} style={{ color: "white" }}>
                                  Risk:{" "}
                                </Title>
                                <Paragraph style={{ color: "white" }}>
                                  {item.risk}
                                </Paragraph>
                                <Title level={5} style={{ color: "white" }}>
                                  Trigger:{" "}
                                </Title>
                                <Paragraph style={{ color: "white" }}>
                                  {item.trigger}
                                </Paragraph>
                                <Title level={5} style={{ color: "white" }}>
                                  Impact:{" "}
                                </Title>
                                <Paragraph style={{ color: "white" }}>
                                  {item.impact}
                                </Paragraph>
                                <Title level={5} style={{ color: "white" }}>
                                  Auto Mitigation{" "}
                                </Title>
                                <List
                                  size="small"
                                  bordered
                                  dataSource={item.execution}
                                  renderItem={(item) => (
                                    <List.Item style={{ color: "white" }}>
                                      {item}
                                    </List.Item>
                                  )}
                                />
                              </div>
                            }
                          >
                            <Tag color="green" key={`${index}-opp`}>
                              {item.title}
                            </Tag>
                          </Tooltip>
                        ))}
                      </Flex>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="System Status">
                <p>
                  <Badge status="success" /> SYSTEM ON
                </p>
                <p>Last Scan: {time.toLocaleTimeString()}</p>
                <p>
                  Next Scan:{" "}
                  {new Date(time.getTime() + 60000).toLocaleTimeString()}
                </p>
                <p>System Uptime: 2 hours 30 minutes</p>
                <p>Last Update: {time.toLocaleTimeString()}</p>
                <p>System Version: 1.0.0</p>
                <p>System Load: 45%</p>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Auto-Executed Missions">
                <List
                  size="small"
                  dataSource={autoExecuted}
                  renderItem={(item) => (
                    <List.Item>
                      <Badge
                        status={
                          item.priority === "Critical" ? "error" : "warning"
                        }
                        style={{ marginRight: 8 }}
                      />
                      {item.title} ({item.priority})
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
  
            <Col span={6}>
              <Card
                title="Mission Control (Selected Targets)"
                style={{ height: 350, overflowY: "auto" }}
              >
                <List
                  size="small"
                  dataSource={missionStack}
                  renderItem={(item) => (
                    <List.Item>
                      <Badge
                        status={item.type === "Risk" ? "error" : "processing"}
                        style={{ marginRight: 8 }}
                      />
                      {item.type} at ({item.x},{item.y})
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
  
            <Col span={18}>
              <Card
                title="System Feed (Live Logs)"
                style={{ height: 750, overflowY: "auto" }}
              >
                <Timeline>
                  {feed.map((entry, idx) => (
                    <Timeline.Item key={idx}>{entry}</Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </Col>
          </Row>
        </div>
      </>
    );
  };
  export default CommandSystemCmp;
  